/**
 * Unified analytics layer for GA4 (gtag.js) and Meta Pixel (fbq).
 *
 * Guard logic:
 *  - Tracking is disabled on localhost / 127.0.0.1 (dev environment).
 *  - Tracking is disabled when the respective measurement/pixel ID is missing.
 *  - Tracking is disabled for admin users.
 *  - When disabled, calls are silently no-ops (dev mode logs to console.debug).
 *  (No consent/opt-out; tracking runs for all non-admin users in production.)
 */
import type { AnalyticsEventName } from './analyticsEvents';

// ---------------------------------------------------------------------------
// Types for the global objects injected by the <script> tags in index.html
// ---------------------------------------------------------------------------
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    fbq?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || '';
const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || '';

const DEV_HOSTS = ['localhost', '127.0.0.1', ''];

let _isAdmin = false;
let _analyticsReady = false;
let _metaInitialized = false;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function isDev(): boolean {
  return DEV_HOSTS.includes(window.location.hostname);
}

function isGAEnabled(): boolean {
  return Boolean(GA_MEASUREMENT_ID) && !isDev() && !_isAdmin && _analyticsReady;
}

function isMetaEnabled(): boolean {
  return Boolean(META_PIXEL_ID) && !isDev() && !_isAdmin && _analyticsReady;
}

function devLog(action: string, ...args: any[]): void {
  if (isDev()) {
    console.debug(`[Analytics:${action}]`, ...args);
  }
}

async function hashUserId(userId: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(userId);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

// ---------------------------------------------------------------------------
// Admin email whitelist (single source of truth for frontend admin checks)
// ---------------------------------------------------------------------------
const ADMIN_EMAILS = [
  'lokesh.jothiram@tringapps.net',
  'vk@voidinc.ai',
  'itslokelokesh06@gmail.com',
  'admin@test.com',
  'admin@example.com',
];

export function isAdminEmail(email: string | undefined | null): boolean {
  if (!email) return false;
  return ADMIN_EMAILS.includes(email.toLowerCase());
}

// ---------------------------------------------------------------------------
// Initialisation
// ---------------------------------------------------------------------------

/**
 * Sets admin flag and marks analytics as ready.  Must be called at least once
 * (by AuthContext) before any tracking fires.  This ensures the initial page
 * view never leaks for admin users — until initAnalytics runs, all tracking
 * calls are silently no-ops because _analyticsReady is false.
 *
 * Also handles deferred Meta Pixel init: fbq('init', pid) is called here
 * (once) instead of in index.html, so the pixel is never activated for admins.
 */
export function initAnalytics(isAdmin: boolean): void {
  _isAdmin = isAdmin;
  _analyticsReady = true;
  devLog('init', { GA_MEASUREMENT_ID, META_PIXEL_ID, isAdmin, ready: true });

  // Deferred Meta Pixel initialization — only once, only for non-admin prod users
  if (!_metaInitialized && isMetaEnabled() && window.fbq) {
    window.fbq('init', META_PIXEL_ID);
    _metaInitialized = true;
    devLog('metaInit', META_PIXEL_ID);
  }
}

/**
 * Identify the logged-in user for cross-session tracking.
 */
export async function identifyUser(userId: string): Promise<void> {
  const hashed = await hashUserId(userId);
  devLog('identifyUser', hashed);

  if (isGAEnabled() && window.gtag) {
    window.gtag('set', { user_id: hashed });
    window.gtag('set', 'user_properties', { cc_user_id: hashed });
  }
}

/**
 * Clear user identity on logout.
 */
export function clearUserIdentity(): void {
  devLog('clearUserIdentity');
  if (isGAEnabled() && window.gtag) {
    window.gtag('set', { user_id: undefined });
  }
}

// ---------------------------------------------------------------------------
// Page views
// ---------------------------------------------------------------------------
const ROUTE_TITLES: Record<string, string> = {
  '/': 'Landing',
  '/login': 'Login',
  '/signup': 'Sign Up',
  '/reset-password': 'Reset Password',
  '/reset-password-confirm': 'Reset Password Confirm',
  '/terms': 'Terms of Service',
  '/privacy': 'Privacy Policy',
  '/dashboard': 'Dashboard',
  '/campaigns': 'Campaigns',
  '/campaigns/new': 'New Campaign',
  // LT-411: legacy /create-agent retired — redirects to /agents.
  '/create-agent': 'Agents',
  '/agents': 'Agents',
  '/agents/new': 'New Agent',
  '/leads': 'Leads',
  '/analytics': 'Analytics',
  '/subscription': 'Subscription',
  '/credits': 'Credits',
  '/inbound-calls': 'Inbound Calls',
  '/inbound-configuration': 'Inbound Configuration',
  '/settings': 'Settings',
  '/feedback': 'Feedback',
  '/support': 'Support',
  '/admin': 'Admin',
  '/admin/feedbacks': 'Admin Feedbacks',
  '/admin/tickets': 'Admin Tickets',
};

function getPageTitle(path: string): string {
  if (ROUTE_TITLES[path]) return ROUTE_TITLES[path];
  if (path.startsWith('/runs/')) return 'Run Detail';
  if (path.startsWith('/admin/users/')) return 'Admin User Detail';
  return path;
}

export function trackPageView(path: string): void {
  const title = getPageTitle(path);
  devLog('pageView', { path, title });

  if (isGAEnabled() && window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: title,
    });
  }

  if (isMetaEnabled() && window.fbq) {
    window.fbq('track', 'PageView');
  }
}

// ---------------------------------------------------------------------------
// Currency normalization
// ---------------------------------------------------------------------------
// Both GA4 and Meta require ISO-4217 currency codes in UPPERCASE. The codebase
// stores currency lowercase ("inr" / "aud") to match the backend convention,
// so analytics is the single point that uppercases on the way out. Uppercasing
// here (vs at every call site) prevents the LT-199 class of bug — a `value`
// payload landing in GA4 with no currency, defaulting to the GA property's
// account currency (historically AUD) and inflating INR revenue.
function normalizeAnalyticsParams(
  params?: Record<string, any>,
): Record<string, any> | undefined {
  if (!params) return params;
  if (typeof params.currency === 'string' && params.currency.length > 0) {
    return { ...params, currency: params.currency.toUpperCase() };
  }
  // Currency missing/empty/non-string: drop the key entirely so we never send
  // currency: "" or currency: undefined to GA4 / Meta. Both treat the absence
  // of the field correctly (GA4 falls back to property currency for the row;
  // Meta omits it from the event); a present-but-empty value is undefined behaviour.
  if ('currency' in params) {
    const { currency: _drop, ...rest } = params;
    return rest;
  }
  return params;
}

// ---------------------------------------------------------------------------
// Custom events — GA4
// ---------------------------------------------------------------------------
export function trackEvent(
  eventName: AnalyticsEventName | string,
  params?: Record<string, any>,
): void {
  const normalized = normalizeAnalyticsParams(params);
  devLog('event', eventName, normalized);

  if (isGAEnabled() && window.gtag) {
    window.gtag('event', eventName, normalized);
  }
}

// ---------------------------------------------------------------------------
// Meta Pixel — standard events
// ---------------------------------------------------------------------------
const META_STANDARD_EVENT_MAP: Record<string, string> = {
  signup_complete: 'CompleteRegistration',
  campaign_created: 'Lead',
  subscription_checkout_started: 'InitiateCheckout',
  payment_success: 'Purchase',
  credit_checkout_started: 'AddToCart',
};

/**
 * Fire a Meta Pixel event. Automatically maps known GA4 event names to
 * standard Meta events; everything else fires as trackCustom.
 */
export function trackMetaEvent(
  eventName: AnalyticsEventName | string,
  params?: Record<string, any>,
): void {
  if (!isMetaEnabled() || !window.fbq) {
    devLog('metaEvent (skipped)', eventName, params);
    return;
  }

  const metaParams = normalizeAnalyticsParams(params);

  const standardEvent = META_STANDARD_EVENT_MAP[eventName];
  if (standardEvent) {
    window.fbq('track', standardEvent, metaParams);
    devLog('metaStandard', standardEvent, metaParams);
  } else {
    window.fbq('trackCustom', eventName, metaParams);
    devLog('metaCustom', eventName, metaParams);
  }
}

// ---------------------------------------------------------------------------
// Convenience: fire to both GA4 + Meta in one call
// ---------------------------------------------------------------------------
export function track(
  eventName: AnalyticsEventName | string,
  params?: Record<string, any>,
): void {
  trackEvent(eventName, params);
  trackMetaEvent(eventName, params);
}
